"use client";

import React, { useMemo } from "react";
import { CardServiceProps } from "@/types";
import { buildAvatarSrc } from "@/utils/buildAvatarSrc";
import { formatCurrency } from "@/utils/formatCurrency";
import { Pencil, Scissors, Trash } from "lucide-react";
import ButtonIcon from "../buttonIcon";
import Switch from "../switch";
import * as S from "./styles";

const CardItem: React.FC<CardServiceProps> = ({
  className,
  titleCard,
  textDescription,
  price,
  time,
  handleUpdate,
  handleRemove,
  cantEdit,
  cantDelete,
  currency,
  dict,
  avatar_url,
  avatar_path,
  category,
  service,
  isActive,
  onToggleActive,
  id,
}) => {
  const avatar = useMemo(
    () =>
      buildAvatarSrc({
        avatar_url,
        avatar_path,
      }),
    [avatar_url, avatar_path],
  );

  const displayDescription = useMemo(() => {
    if (!textDescription || textDescription.trim() === "") {
      return dict?.["No service description"] || "Sem descrição de serviço";
    }
    return textDescription;
  }, [textDescription, dict]);

  const formatTime = useMemo(() => {
    if (!time) return "-";

    if (typeof time === "string" && time.includes(":")) {
      const parts = time.split(":");
      const hours = parseInt(parts[0] || "0", 10);
      const minutes = parseInt(parts[1] || "0", 10);
      const totalMinutes = hours * 60 + minutes;

      if (totalMinutes === 0) return "-";
      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}min`;
      }
      if (hours > 0) {
        return `${hours}h`;
      }
      if (minutes > 0) {
        return `${minutes}min`;
      }
      return "-";
    }

    const minutes =
      typeof time === "number" ? time : parseInt(String(time), 10);
    if (isNaN(minutes) || minutes <= 0) return "-";

    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes > 0) {
        return `${hours}h ${remainingMinutes}min`;
      }
      return `${hours}h`;
    }

    return `${minutes}min`;
  }, [time]);

  return (
    <S.CardService className={className}>
      <S.BoxBody>
        <S.HeaderCard>
          {avatar ? (
            <S.ImageEmployee as="img" src={avatar} alt={titleCard} />
          ) : (
            <S.BoxIcon>
              <Scissors />
            </S.BoxIcon>
          )}
          <S.InfoHeader>
            <S.TitleCard>{titleCard}</S.TitleCard>
          </S.InfoHeader>
        </S.HeaderCard>
        <S.BoxAlign>
          <S.Control>
            <S.TitleControl>{dict?.Price || "Preço"}</S.TitleControl>
            <S.Value>{formatCurrency(Number(price) || 0, currency)}</S.Value>
          </S.Control>
          {(category || time) && (
            <S.Control>
              <S.TitleControl>
                {category
                  ? dict?.Category || "Categoria"
                  : dict?.Time || "Duração"}
              </S.TitleControl>
              <S.Value>{category || formatTime}</S.Value>
            </S.Control>
          )}
        </S.BoxAlign>
        <S.BodyCard>
          {/*<S.TextDescription>{displayDescription}</S.TextDescription> */}
          {service && (
            <S.ListItemPlans>
              <S.ItemPlans>{service}</S.ItemPlans>
            </S.ListItemPlans>
          )}
        </S.BodyCard>
        {isActive !== undefined && (
          <S.StatusRow>
            <span
              style={{
                color: isActive ? "green" : "red",
                fontWeight: 600,
              }}
            >
              {isActive ? "Ativo" : "Inativo"}
            </span>
            <Switch
              checked={isActive}
              onChange={() => onToggleActive && id && onToggleActive(id)}
            />
          </S.StatusRow>
        )}
      </S.BoxBody>
      <S.FooterCard>
        {cantEdit && (
          <ButtonIcon
            icon={<Pencil />}
            hovercolor="green"
            onClick={handleUpdate}
          />
        )}
        {cantDelete && (
          <ButtonIcon
            icon={<Trash />}
            hovercolor="red"
            onClick={handleRemove}
          />
        )}
      </S.FooterCard>
    </S.CardService>
  );
};

export default CardItem;
